import CHOICES from "CHOICES";
import { mutate } from "utils/relay";
import {
  AUTH_TOKEN,
  ENV_MAPPER,
  NDC_CONNECT_ENDPOINT,
  REFRESH_TOKEN,
  SELECTED_ORGANIZATION,
  TOKEN_TTL,
} from "utils/constants";
import {
  decodeReadStringifyObj,
  emptyProxyObject,
  getEnv,
  ProxyObjectType,
} from "utils/utils";
import { Action, Thunk, Computed, thunk, action, computed } from "easy-peasy";
import { Cookies } from "react-cookie";
import moment from "moment";
import { graphqlRequest, setAuthRefreshHandler } from "api/client";

const cookie = new Cookies();

const readStoredAuthToken = () => {
  const storedToken =
    window.sessionStorage.getItem(AUTH_TOKEN) ||
    window.localStorage.getItem(AUTH_TOKEN);

  if (storedToken) {
    try {
      return JSON.parse(storedToken);
    } catch {
      return storedToken;
    }
  }

  return cookie.get(AUTH_TOKEN) || null;
};

const UK = CHOICES.UserKind;

export const cookieDomain = (env: string) =>
  env === ENV_MAPPER.production
    ? ".v3.nuflights.com"
    : env === ENV_MAPPER.connect
      ? ".connect.nuflights.com"
      : env === ENV_MAPPER.development
        ? `dev.nuflights.com`
        : `.staging.llc.nuflights.com`;

export interface CurrentUserModel {
  username: string;
  email: string;
}

export type IdpTokenResponse = {
  accessToken: string;
  tokenType?: string;
  expiresIn?: number;
};

export interface SelectedOrganization {
  group?: string;
  group_name?: string;
  is_direct_member?: boolean;
  roles?: string[];
  permissions?: string[];
  [key: string]: any;
}

export interface AuthModel {
  currentUser: CurrentUserModel | ProxyObjectType;
  authToken: string | null;
  isLoggedIn: Computed<AuthModel, boolean>;
  isAdmin: Computed<AuthModel, boolean>;
  setAuthToken: Action<AuthModel, string | null>;
  setCurrentUser: Action<AuthModel, CurrentUserModel | ProxyObjectType>;
  updateAuthToken: Thunk<AuthModel, any>;
  clearAuthToken: Thunk<AuthModel>;
  fetchAuthToken: Thunk<AuthModel, any>;
  initRefreshToken: Thunk<AuthModel, boolean>;
  permissions: any;
  setPermissions: Action<AuthModel, any>;
  updatePermissions: Thunk<AuthModel, string[] | string | undefined>;
  refreshToken: string | null;
  selectedOrganization: SelectedOrganization | null;
  setSelectedOrganization: Action<AuthModel, SelectedOrganization | null>;
  setRefreshToken: Action<AuthModel, string | null>;
  updateRefreshToken: Thunk<AuthModel, any>;
  clearRefreshToken: Thunk<AuthModel>;
  refreshIntervalId: NodeJS.Timeout | null;
  setRefreshIntervalId: Action<AuthModel, NodeJS.Timeout | null>;
}

const AuthStore: AuthModel = {
  currentUser: emptyProxyObject,
  permissions: {},
  authToken: readStoredAuthToken(),
  refreshToken: cookie.get(REFRESH_TOKEN),
  selectedOrganization: decodeReadStringifyObj(SELECTED_ORGANIZATION) ?? null,
  refreshIntervalId: null,

  isLoggedIn: computed((state: any) => state.currentUser !== emptyProxyObject),
  isAdmin: computed((state: any) => state.currentUser.kind === UK.ADMIN),

  setAuthToken: action((state: any, authToken) => {
    state.authToken = authToken;
  }),

  setRefreshToken: action((state: any, refreshToken) => {
    state.refreshToken = refreshToken;
  }),

  setSelectedOrganization: action((state: any, selectedOrganization) => {
    state.selectedOrganization = selectedOrganization;
  }),

  setCurrentUser: action((state: any, currentUser) => {
    state.currentUser = currentUser;
  }),

  setPermissions: action((state: any, permissions) => {
    state.permissions = permissions;
  }),

  setRefreshIntervalId: action((state: any, intervalId) => {
    state.refreshIntervalId = intervalId;
  }),

  updatePermissions: thunk((actions, permissions) => {
    let permissionsObj: any = {};

    if (Array.isArray(permissions)) {
      permissions.forEach((permission: string) => {
        permissionsObj[permission] = true;
      });
    } else if (permissions) {
      permissions
        .split(" ")
        .forEach((permission: string) => (permissionsObj[permission] = true));
    }

    actions.setPermissions(permissionsObj);
  }),

  updateAuthToken: thunk((actions, authToken) => {
    getEnv() !== ENV_MAPPER.local
      ? cookie.set(AUTH_TOKEN, authToken.self, {
          domain: cookieDomain(getEnv()),
          path: "/",
          sameSite: "strict",
          secure: true,
          expires:
            authToken.expiry instanceof Date
              ? authToken.expiry
              : new Date(authToken.expiry),
        })
      : cookie.set(AUTH_TOKEN, authToken.self, {
          path: "/",
          sameSite: "strict",
          secure: true,
        });
    actions.setAuthToken(authToken.self);
  }),

  updateRefreshToken: thunk((actions, refreshToken) => {
    getEnv() !== ENV_MAPPER.local
      ? cookie.set(REFRESH_TOKEN, refreshToken.self, {
          domain: cookieDomain(getEnv()),
          path: "/",
          sameSite: "strict",
          secure: true,
          expires:
            refreshToken.expiry instanceof Date
              ? refreshToken.expiry
              : new Date(refreshToken.expiry),
        })
      : cookie.set(REFRESH_TOKEN, refreshToken.self, {
          path: "/",
          sameSite: "strict",
          secure: true,
        });
    actions.setRefreshToken(refreshToken.self);
  }),

  clearAuthToken: thunk(async (actions, payload, { getState }) => {
    const state = getState();
    if (state.refreshIntervalId) {
      clearInterval(state.refreshIntervalId);
      actions.setRefreshIntervalId(null);
    }

    window.localStorage.clear();
    window.sessionStorage.clear();
    getEnv() !== ENV_MAPPER.local
      ? await cookie.remove(AUTH_TOKEN, {
          domain: cookieDomain(getEnv()),
          path: "/",
        })
      : await cookie.remove(AUTH_TOKEN, { path: "/" });
    actions.setAuthToken(null);
    actions.setCurrentUser(emptyProxyObject);
    actions.setPermissions([]);
  }),

  clearRefreshToken: thunk(async (actions, payload, { getState }) => {
    const state = getState();
    if (state.refreshIntervalId) {
      clearInterval(state.refreshIntervalId);
      actions.setRefreshIntervalId(null);
    }

    window.localStorage.clear();
    getEnv() !== ENV_MAPPER.local
      ? await cookie.remove(REFRESH_TOKEN, {
          domain: cookieDomain(getEnv()),
          path: "/",
        })
      : await cookie.remove(REFRESH_TOKEN, { path: "/" });
    actions.setRefreshToken(null);
    actions.setCurrentUser(emptyProxyObject);
  }),

  fetchAuthToken: thunk(
    (actions, { mutation, input, onSuccess, onFailure }) => {
      mutate({
        mutation,
        input: input,
        onSuccess: function (data: any) {
          let token: any = {
            self: "",
            expiry: new Date(),
          };
          let refreshToken: any = {
            self: "",
            expiry: new Date(),
          };
          let unixTime = Number(moment.unix(data.login.refreshExpiresIn || 0));
          let date = new Date(unixTime);
          if (data.login) {
            token.self = data.login.token;
            token.expiry = date;
            refreshToken.self = data.login.refreshToken;
            refreshToken.expiry = date;
          } else if (data.socialAuth) {
            token = data.socialAuth.token;
          }
          actions.updateAuthToken(token);
          actions.updateRefreshToken(refreshToken);
          onSuccess(data);
        },
        onFailure: function (messages: []) {
          actions.clearAuthToken();
          actions.clearRefreshToken();
          onFailure(messages);
        },
      });
    }
  ),

  initRefreshToken: thunk((actions, initialFetch = true, { getState }) => {
    const refreshAccessToken = async () => {
      try {
        const query = `
          mutation idpRefresh {
            idpRefresh {
              accessToken
              tokenType
              expiresIn
            }
          }
        `;

        const data = await graphqlRequest<{ idpRefresh: IdpTokenResponse }>(
          query,
          undefined,
          undefined,
          NDC_CONNECT_ENDPOINT
        );

        const accessToken = data.idpRefresh.accessToken;
        const expiresIn = data.idpRefresh.expiresIn ?? 300;
        const expiry = new Date(Date.now() + expiresIn * 1000);

        actions.updateAuthToken({ self: accessToken, expiry });

        const selectedOrganization =
          decodeReadStringifyObj(SELECTED_ORGANIZATION) ?? null;
        actions.setSelectedOrganization(selectedOrganization);
        actions.updatePermissions(selectedOrganization?.permissions);

        return true;
      } catch (error) {
        // console.log("failed in idpRefresh token flow", error);
        actions.clearAuthToken();
        actions.clearRefreshToken();
        return false;
      }
    };

    setAuthRefreshHandler(refreshAccessToken);

    if (initialFetch) {
      void refreshAccessToken();
    }

    const state = getState();
    if (state.refreshIntervalId) {
      clearInterval(state.refreshIntervalId);
      actions.setRefreshIntervalId(null);
    }

    const intervalId = setInterval(() => {
      void refreshAccessToken();
    }, TOKEN_TTL);

    actions.setRefreshIntervalId(intervalId);
  }),
};

export default AuthStore;
